import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Upload,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CameraOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import useUsers from "hooks/userManagement/useUsers";
import { useFileUpload } from "hooks/useFileUpload";
import useAppConfig from "hooks/access-control/useAppConfig";
import { startEmailVerification, startPhoneVerification, verifyEmail, verifyPhone } from "api/authApi";
import apiClient from "utils/apiClient";
import GooglePlacesAutocompleteInput from "common/GooglePlacesAutocompleteInput";
import OlitaskHeaderIcon from "components/olitask/OlitaskHeaderIcon";
import { locationFieldsFromPlaceDetails } from "utils/googlePlaces";
import {
  GENDER_OPTIONS,
  STATUS_OPTIONS,
  cleanValue,
  formatDateTime,
  getDisplayName,
} from "./profileUtils";

const OLIVEEB = {
  green: "#12B76A",
  greenDark: "#0F7D34",
  blue: "#2F4AE3",
  red: "#D10A06",
  yellow: "#F4B400",
  text: "#101828",
  subtext: "#667085",
  border: "#E4E7EC",
  card: "#FFFFFF",
  card2: "#F9FAFB",
  bg: "#F6F8FB",
};

const profileGenderOptions = GENDER_OPTIONS.filter((item) => item.value);

const callingCodeOptions = [
  { label: "United States (+1)", value: "+1" },
  { label: "Canada (+1)", value: "+1" },
  { label: "United Kingdom (+44)", value: "+44" },
  { label: "Ghana (+233)", value: "+233" },
  { label: "Kenya (+254)", value: "+254" },
  { label: "Nigeria (+234)", value: "+234" },
];

const fieldGroups = [
  {
    title: "Identity",
    description: "Edit your core account details one field at a time.",
    fields: [
      { name: "firstName", label: "First Name", icon: <UserOutlined /> },
      { name: "middleName", label: "Middle Name", icon: <UserOutlined /> },
      { name: "lastName", label: "Last Name", icon: <UserOutlined /> },
      { name: "username", label: "Username", icon: <UserOutlined />, editable: false },
    ],
  },
  {
    title: "Contact",
    description: "Keep your contact details accurate and up to date.",
    fields: [
      { name: "email", label: "Email", icon: <MailOutlined />, editable: false },
      { name: "phone", label: "Phone", icon: <PhoneOutlined /> },
    ],
  },
  {
    title: "Profile",
    description: "Personal attributes used throughout the PM tool.",
    fields: [
      { name: "gender", label: "Gender", type: "select", options: GENDER_OPTIONS },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS },
    ],
  },
];

const fileUrlFromValue = (value, visited = new Set()) => {
  if (!value) return null;
  if (typeof value === "string") {
    return /^(https?:|data:|blob:)/i.test(value) ? value : null;
  }
  if (typeof value !== "object" || visited.has(value)) return null;
  visited.add(value);

  const direct = [
    value.signedUrl,
    value.signed_url,
    value.downloadUrl,
    value.download_url,
    value.publicUrl,
    value.public_url,
    value.fileUrl,
    value.file_url,
    value.previewUrl,
    value.preview_url,
    value.uri,
    value.url,
  ].find((item) => typeof item === "string" && /^(https?:|data:|blob:)/i.test(item));
  if (direct) return direct;

  return [value.file, value.metadata, value.meta, value.data]
    .map((item) => fileUrlFromValue(item, visited))
    .find(Boolean) || null;
};

const fileIdFromValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return /^(https?:|data:|blob:)/i.test(value) ? "" : value;
  if (typeof value !== "object") return "";
  return cleanValue(
    value.id ||
      value.fileId ||
      value.file_id ||
      value.fid ||
      value.file?.id ||
      value.file?.fileId ||
      value.metadata?.id ||
      value.meta?.id,
  );
};

const readPhotoUrl = async (value, getFileMetadata, getFileSignedUrl) => {
  const raw = cleanValue(value);
  const directUrl = fileUrlFromValue(value);
  if (directUrl) return directUrl;

  const fileId = fileIdFromValue(value) || raw;
  if (!fileId || /^(https?:|data:|blob:)/i.test(fileId)) return null;

  try {
    const meta = getFileMetadata ? await getFileMetadata(fileId) : null;
    const metaUrl = fileUrlFromValue(meta);
    if (metaUrl) return metaUrl;
  } catch {
    // Fall through to signed URL lookup.
  }

  try {
    return getFileSignedUrl ? await getFileSignedUrl(fileId) : null;
  } catch {
    return null;
  }
};

const cacheBustPhotoUrl = (url) => {
  if (!url || !/^https?:\/\//i.test(url)) return url || null;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}cb=${Date.now()}`;
};

const FieldCard = ({
  label,
  value,
  type = "text",
  options = [],
  icon,
  helperText,
  editable = true,
  onSave,
  loading = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [draft, setDraft] = useState(cleanValue(value));
  const hoverSupported =
    typeof window !== "undefined"
      ? window.matchMedia?.("(hover: hover)")?.matches ?? true
      : true;

  useEffect(() => {
    setDraft(cleanValue(value));
  }, [value]);

  const displayValue = useMemo(() => {
    if (type === "select") {
      const option = options.find((item) => String(item.value) === String(value));
      return option?.label || cleanValue(value) || "Not provided";
    }
    return cleanValue(value) || "Not provided";
  }, [options, type, value]);

  const handleSave = async () => {
    if (!onSave) return;
    const nextValue = type === "select" ? draft || "" : draft;
    if (cleanValue(nextValue) === cleanValue(value)) {
      setEditing(false);
      return;
    }
    await onSave(nextValue);
    setEditing(false);
  };

  return (
    <Card
      size="small"
      className="custom-shadow"
      style={{
        borderRadius: 14,
        border: "1px solid #e5e7eb",
        background: "#fff",
      }}
      bodyStyle={{ padding: 14 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="d-flex align-items-center gap-3"
        style={{ minHeight: 44 }}
      >
        <div
          className="d-flex align-items-center gap-2"
          style={{ minWidth: 170, flexShrink: 0 }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "#f8fafc",
              border: "1px solid #e5e7eb",
              display: "grid",
              placeItems: "center",
              color: "#475569",
              flexShrink: 0,
            }}
          >
            {icon || <UserOutlined />}
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>
              {label}
            </div>
            {helperText && <div style={{ fontSize: 12, color: "#64748b" }}>{helperText}</div>}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {!editing ? (
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: "#111827",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displayValue}
            </div>
          ) : type === "select" ? (
            <Select
              value={draft || undefined}
              options={options}
              onChange={(nextValue) => setDraft(nextValue || "")}
              style={{ width: "100%" }}
            />
          ) : (
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={label}
              autoComplete="off"
            />
          )}
        </div>

        {editable && onSave && (
          <Button
            type="text"
            size="small"
            icon={editing ? <SaveOutlined /> : <EditOutlined />}
            loading={loading}
            onClick={editing ? handleSave : () => setEditing(true)}
            aria-label={editing ? `Save ${label}` : `Edit ${label}`}
            title={editing ? `Save ${label}` : `Edit ${label}`}
            style={{
              color: "#0f172a",
              marginLeft: "auto",
              opacity: hoverSupported ? (hovered || editing ? 1 : 0) : 1,
              pointerEvents: hoverSupported ? (hovered || editing ? "auto" : "none") : "auto",
              transition: "opacity 0.15s ease",
            }}
          />
        )}
      </div>
    </Card>
  );
};

const PhotoCard = ({
  title,
  description,
  preview,
  onUpload,
  loading = false,
  badge,
}) => (
  <Card
    className="custom-shadow h-100"
    style={{ borderRadius: 16, border: "1px solid #e5e7eb" }}
    bodyStyle={{ padding: 18 }}
  >
    <div className="d-flex align-items-start justify-content-between gap-3">
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{title}</div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{description}</div>
      </div>
      {badge}
    </div>

    <div className="d-flex flex-column align-items-center justify-content-center text-center mt-3">
      <Avatar
        size={96}
        src={preview || undefined}
        icon={!preview && <UserOutlined />}
        style={{
          background: preview ? undefined : "linear-gradient(135deg, #111827, #475569)",
          color: "#fff",
          fontSize: 30,
          marginBottom: 14,
        }}
      >
        {!preview ? "U" : null}
      </Avatar>

      <Upload beforeUpload={() => false} showUploadList={false} accept="image/*,.heic,.heif" onChange={({ file }) => onUpload(file.originFileObj || file)}>
        <Button icon={<CameraOutlined />} loading={loading}>
          Update photo
        </Button>
      </Upload>
    </div>
  </Card>
);

const PasswordCard = ({ onSubmit, loading = false, enabled = true }) => {
  const [form] = Form.useForm();

  if (!enabled) {
    return (
      <Card className="custom-shadow" style={{ borderRadius: 16 }}>
        <Typography.Text type="secondary">Password changes are only available for your own profile.</Typography.Text>
      </Card>
    );
  }

  const handleFinish = async (values) => {
    await onSubmit(values);
    form.resetFields();
  };

  return (
    <Card
      className="custom-shadow"
      style={{ borderRadius: 16, border: "1px solid #e5e7eb" }}
      bodyStyle={{ padding: 18 }}
    >
      <div className="d-flex align-items-center gap-2 mb-2">
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: "#f8fafc",
            border: "1px solid #e5e7eb",
            display: "grid",
            placeItems: "center",
          }}
        >
          <LockOutlined />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Security</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>Change your password and review security details.</div>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label="Current password" name="currentPassword" rules={[{ required: true, message: "Current password is required" }]}>
              <Input.Password />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="New password" name="newPassword" rules={[{ required: true, message: "New password is required" }]}>
              <Input.Password />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Confirm password"
              name="confirmPassword"
              dependencies={["newPassword"]}
              rules={[
                { required: true, message: "Confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) return Promise.resolve();
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>

        <div className="d-flex justify-content-end">
          <Button type="primary" htmlType="submit" loading={loading}>
            Update Password
          </Button>
        </div>
      </Form>
    </Card>
  );
};

const requesterRequiredProperties = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "birthDate", label: "Date of Birth" },
  { key: "gender", label: "Gender" },
  { key: "email", label: "Email" },
  { key: "address", label: "Address" },
  { key: "profilePhoto", label: "Profile Photo" },
];

const helperRequiredProperties = [
  ...requesterRequiredProperties,
  { key: "securityPhoto", label: "Security Photo" },
  { key: "identityVerification", label: "Identity Verification" },
  { key: "helperProfile", label: "Helper Profile" },
];

const isBlank = (value) => value === null || value === undefined || String(value).trim() === "" || String(value).trim() === "None";

const isTruthyFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return ["true", "verified", "active", "yes", "approved", "success", "successful", "completed", "complete", "passed", "pass", "valid"].includes(normalized);
  }
  return false;
};

const bestAddress = (profile = {}) =>
  profile.permanentAddress ||
  profile.residentialAddress ||
  profile.workAddress ||
  profile.schoolAddress ||
  profile.billingAddress ||
  null;

const addressString = (profile = {}) => {
  const address = bestAddress(profile);
  if (!address) return "Not set";
  const parts = [
    address.street,
    address.city,
    address.state,
    address.zipCode || address.zip,
    address.country,
  ].filter((item) => !isBlank(item));
  return parts.length ? parts.join(", ") : "Not set";
};

const digitsOnly = (value) => String(value || "").replace(/[^\d]/g, "");

const addressDraftFromProfile = (profile = {}) => {
  const address = bestAddress(profile) || {};
  return {
    street: cleanValue(address.street),
    city: cleanValue(address.city),
    state: cleanValue(address.state),
    zip: cleanValue(address.zipCode || address.zip),
    country: cleanValue(address.country || "USA"),
  };
};

const normalizeAddressInput = (address = {}) => ({
  street: cleanValue(address.street).trim(),
  city: cleanValue(address.city).trim(),
  state: cleanValue(address.state).trim().toUpperCase(),
  zipCode: digitsOnly(address.zip || address.zipCode),
  country: cleanValue(address.country).trim(),
});

const hasAddress = (profile = {}) => {
  const address = bestAddress(profile);
  return Boolean(address && !isBlank(address.street) && !isBlank(address.city) && !isBlank(address.country));
};

const hasIdentityVerification = (profile = {}) => {
  const verification = profile.userIdentityVerification;
  if (!verification || verification === "pending" || verification === "None") return false;
  return isIdentityVerificationVerified(verification);
};

const isIdentityVerificationVerified = (verification = {}) => {
  if (!verification || typeof verification !== "object") return false;
  if (verification.isVerified === false || verification.verified === false || verification.isVersified === false) return false;
  if (verification.requiresReview === true) return false;

  const statusText = [
    verification.status,
    verification.verificationStatus,
    verification.verificationDecision,
    verification.decision,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  const negativeStatuses = new Set([
    "auto_flagged",
    "flagged",
    "requires_review",
    "manual_review",
    "review",
    "pending",
    "rejected",
    "denied",
    "failed",
    "fail",
    "invalid",
  ]);
  if (statusText.some((value) => negativeStatuses.has(value))) return false;

  return Boolean(
    isTruthyFlag(verification.isVerified) ||
      isTruthyFlag(verification.verified) ||
      isTruthyFlag(verification.isVersified) ||
      statusText.some((value) => isTruthyFlag(value)),
  );
};

const hasHelperProfile = (profile = {}) =>
  Boolean(profile.helperProfile || profile.helperProfileStatus || profile.helperCreatedAt || profile.helperUpdatedAt);

const requiredPropertiesForRole = (role) =>
  String(role || "").toUpperCase() === "HELPER" ? helperRequiredProperties : requesterRequiredProperties;

const missingRequiredProperty = (profile, key) => {
  switch (key) {
    case "firstName":
    case "lastName":
    case "birthDate":
    case "gender":
    case "email":
    case "profilePhoto":
    case "securityPhoto":
      return isBlank(profile?.[key]);
    case "address":
      return !hasAddress(profile);
    case "identityVerification":
      return !hasIdentityVerification(profile);
    case "helperProfile":
      return !hasHelperProfile(profile);
    default:
      return false;
  }
};

const maskSsn = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "Not set";
  return "***-**-****";
};

const maskDateOfBirth = (value) => (cleanValue(value) ? "****-**-**" : "Not set");

const formatSsn = (value) => {
  const digits = digitsOnly(value).slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const isValidSsn = (value) => digitsOnly(value).length === 9;

const normalizeCareRoleName = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const hasCareOwnerOrAdminRole = (roles = []) => {
  const roleSet = new Set((Array.isArray(roles) ? roles : [roles]).map(normalizeCareRoleName).filter(Boolean));
  return roleSet.has("care_owner") || roleSet.has("care_admin") || roleSet.has("auto_owner") || roleSet.has("auto_admin");
};

const splitE164ToCodeAndNational = (value) => {
  const raw = cleanValue(value);
  const digits = digitsOnly(raw);
  if (!digits) return { code: "+1", national: "" };
  const normalized = raw.startsWith("+") ? `+${digits}` : `+1${digits}`;
  const codes = [...new Set(callingCodeOptions.map((item) => item.value))]
    .sort((a, b) => b.length - a.length);
  const code = codes.find((item) => normalized.startsWith(item)) || "+1";
  return { code, national: normalized.slice(code.length) };
};

const isAtLeast18 = (value) => {
  const date = dayjs(value);
  if (!date.isValid()) return false;
  return dayjs().diff(date, "year") >= 18;
};

const genderLabel = (value) => {
  const option = GENDER_OPTIONS.find((item) => String(item.value) === String(value));
  return option?.label || cleanValue(value) || "Not set";
};

const SectionCard = ({ title, children, hideTopDivider = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card
      className="custom-shadow olitask-profile-section"
      style={{
        borderRadius: 18,
        border: `1px solid ${OLIVEEB.border}`,
        background: OLIVEEB.card,
        marginBottom: 18,
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
      }}
      bodyStyle={{ padding: 16 }}
    >
      <button
        type="button"
        className="d-flex align-items-center justify-content-between gap-2 w-100"
        onClick={() => setOpen((prev) => !prev)}
        style={{ background: "transparent", border: 0, padding: 0, textAlign: "left" }}
      >
        <div>
          <Typography.Title level={4} style={{ margin: 0, color: OLIVEEB.greenDark, fontSize: 17, fontWeight: 900 }}>
            {title}
          </Typography.Title>
          {!open ? (
            <div style={{ marginTop: 4, color: "#B54708", fontSize: 12, fontWeight: 900 }}>
              Tap to expand and review these details.
            </div>
          ) : null}
        </div>
        <div
          className="d-flex align-items-center gap-1"
          style={{
            border: `1px solid ${open ? "#A6F4C5" : "#D1E9FF"}`,
            background: open ? "#ECFDF3" : "#EFF8FF",
            borderRadius: 999,
            padding: "7px 11px",
            color: open ? OLIVEEB.greenDark : OLIVEEB.blue,
            fontWeight: 900,
            fontSize: 12,
          }}
        >
          <span>{open ? "Open" : "Closed"}</span>
          <OlitaskHeaderIcon name="chevron-forward" size={15} />
        </div>
      </button>
      {open ? <div style={{ marginTop: hideTopDivider ? 10 : 14 }}>{children}</div> : null}
    </Card>
  );
};

const MobileFieldRow = ({
  label,
  value,
  required,
  missing,
  verified,
  editable = true,
  type = "text",
  options = [],
  loading,
  onSave,
  onVerify,
  verifying = false,
  children,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(cleanValue(value));

  useEffect(() => {
    setDraft(cleanValue(value));
  }, [value]);

  const displayValue = type === "select" ? genderLabel(value) : cleanValue(value) || "Not set";
  const dateValue = draft && dayjs(draft).isValid() ? dayjs(draft) : null;
  const save = async () => {
    if (!onSave) return;
    await onSave(draft);
    setEditing(false);
  };

  return (
    <div
      className="d-flex align-items-start justify-content-between gap-3"
      style={{
        borderTop: `1px solid #EEF2F6`,
        padding: "14px 0",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="d-flex align-items-center flex-wrap gap-2" style={{ marginBottom: 6 }}>
          <span style={{ color: OLIVEEB.subtext, fontSize: 12, fontWeight: 900, letterSpacing: ".02em", textTransform: "uppercase" }}>
            {label}
          </span>
          {required ? (
            <Tag color="blue" style={{ borderRadius: 999, marginInlineEnd: 0, fontWeight: 800 }}>
              Required
            </Tag>
          ) : null}
          {missing ? (
            <Tag color="red" style={{ borderRadius: 999, marginInlineEnd: 0, fontWeight: 800 }}>
              Needs Update
            </Tag>
          ) : null}
        </div>

        {children || (!editing ? (
          <>
            <div style={{ color: OLIVEEB.text, fontSize: 15, lineHeight: "21px", fontWeight: 800, overflowWrap: "anywhere" }}>
              {displayValue}
            </div>
            {verified !== undefined ? (
              verified ? (
                <div
                  className="d-inline-flex align-items-center gap-1 mt-2"
                  style={{ background: "#E8F5E9", color: "#4CAF50", borderRadius: 999, padding: "5px 9px", fontWeight: 800, fontSize: 12 }}
                >
                  <OlitaskHeaderIcon name="checkmark-circle" size={16} />
                  Verified
                </div>
              ) : cleanValue(value) && editable ? (
                <button
                  type="button"
                  onClick={onVerify}
                  disabled={!onVerify || verifying}
                  className="d-inline-flex align-items-center mt-2"
                  style={{ background: OLIVEEB.blue, border: 0, color: "#fff", borderRadius: 8, padding: "7px 14px", fontWeight: 800, fontSize: 13, cursor: onVerify ? "pointer" : "default" }}
                >
                  {verifying ? "Sending..." : `Verify ${label}`}
                </button>
              ) : null
            ) : null}
          </>
        ) : type === "date" ? (
          <DatePicker
            value={dateValue}
            format="YYYY-MM-DD"
            placeholder="YYYY-MM-DD"
            onChange={(_, dateString) => setDraft(dateString || "")}
            disabledDate={(current) => current && current.isAfter(dayjs().subtract(18, "year").endOf("day"))}
            style={{ width: "100%", height: 42, borderRadius: 14 }}
          />
        ) : type === "select" ? (
          <Select value={draft || undefined} options={options} onChange={(next) => setDraft(next || "")} style={{ width: "100%" }} />
        ) : (
          <Input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={label} autoComplete="off" />
        ))}
      </div>

      {editable && onSave && !children ? (
        <Space size={4}>
          {editing ? (
            <>
              <Button type="text" onClick={() => setEditing(false)} icon={<OlitaskHeaderIcon name="close-circle" size={24} />} style={{ color: OLIVEEB.red }} />
              <Button type="text" loading={loading} onClick={save} icon={<OlitaskHeaderIcon name="checkmark-circle" size={24} />} style={{ color: OLIVEEB.green }} />
            </>
          ) : (
            <Button type="text" onClick={() => setEditing(true)} icon={<OlitaskHeaderIcon name="pencil" size={18} />} style={{ color: OLIVEEB.greenDark }} />
          )}
        </Space>
      ) : null}
    </div>
  );
};

const ProfilePhotoTile = ({ title, preview, timestamp, iconName, actionColor, onUpload, onPreview, uploading, editable = true }) => (
  <div
    className="d-flex flex-column align-items-center text-center"
    style={{
      flex: "1 1 260px",
      borderRadius: 16,
      border: "1px solid #EEF2F6",
      background: OLIVEEB.card2,
      padding: "14px 12px",
      minWidth: 220,
    }}
  >
    <div style={{ color: OLIVEEB.subtext, fontSize: 12, fontWeight: 900, letterSpacing: ".02em", textTransform: "uppercase", marginBottom: 8 }}>
      {title}
    </div>
    <div style={{ width: 120, height: 120, position: "relative" }}>
      <button
        type="button"
        onClick={() => preview && onPreview?.()}
        disabled={!preview}
        aria-label={preview ? `Preview ${title}` : `${title} not available`}
        style={{
          background: "transparent",
          border: 0,
          borderRadius: 999,
          cursor: preview ? "zoom-in" : "default",
          display: "block",
          height: 120,
          padding: 0,
          width: 120,
        }}
      >
        <Avatar
          key={preview || title}
          size={120}
          src={preview || undefined}
          icon={!preview && <UserOutlined />}
          style={{
            border: "3px solid #D1FADF",
            background: preview ? "#fff" : "#EEF2F6",
            color: OLIVEEB.subtext,
            fontWeight: 800,
          }}
        />
      </button>
      {editable ? (
        <Upload beforeUpload={() => false} showUploadList={false} accept="image/*,.heic,.heif" onChange={({ file }) => onUpload(file.originFileObj || file)}>
          <Button
            shape="circle"
            loading={uploading}
            icon={<OlitaskHeaderIcon name={iconName} size={17} />}
            style={{
              position: "absolute",
              right: 4,
              bottom: 4,
              width: 36,
              height: 36,
              display: "grid",
              placeItems: "center",
              background: actionColor,
              borderColor: "rgba(255,255,255,.8)",
              color: "#fff",
              boxShadow: "0 10px 18px rgba(15, 23, 42, 0.18)",
            }}
          />
        </Upload>
      ) : null}
    </div>
    {timestamp ? (
      <div style={{ marginTop: 8, color: OLIVEEB.subtext, fontSize: 11, fontWeight: 700 }}>
        Last Updated At: {formatDateTime(timestamp)}
      </div>
    ) : null}
  </div>
);

const PhoneFieldRow = ({ value, verified, loading, onSave, onVerify, verifying = false, editable = true }) => {
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState("+1");
  const [national, setNational] = useState("");

  useEffect(() => {
    if (!editing) {
      const split = splitE164ToCodeAndNational(value);
      setCode(split.code);
      setNational(split.national);
    }
  }, [editing, value]);

  const e164 = national ? `${code}${digitsOnly(national)}` : "";

  return (
    <MobileFieldRow label="Phone" value={value} verified={verified} editable={false}>
      {!editing ? (
        <>
          <div style={{ color: OLIVEEB.text, fontSize: 15, lineHeight: "21px", fontWeight: 800 }}>
            {cleanValue(value) || "Not set"}
          </div>
          {verified ? (
            <div className="d-inline-flex align-items-center gap-1 mt-2" style={{ background: "#E8F5E9", color: "#4CAF50", borderRadius: 999, padding: "5px 9px", fontWeight: 800, fontSize: 12 }}>
              <OlitaskHeaderIcon name="checkmark-circle" size={16} />
              Verified
            </div>
          ) : cleanValue(value) && editable ? (
            <button
              type="button"
              className="d-inline-flex align-items-center mt-2"
              onClick={onVerify}
              disabled={!onVerify || verifying}
              style={{ background: OLIVEEB.blue, border: 0, color: "#fff", borderRadius: 8, padding: "7px 14px", fontWeight: 800, fontSize: 13, cursor: onVerify ? "pointer" : "default" }}
            >
              {verifying ? "Sending..." : "Verify Phone"}
            </button>
          ) : null}
          {editable ? (
            <Button type="text" onClick={() => setEditing(true)} icon={<OlitaskHeaderIcon name="pencil" size={18} />} style={{ marginTop: 8, paddingLeft: 0, color: OLIVEEB.greenDark, fontWeight: 850 }}>
              Edit Phone
            </Button>
          ) : null}
        </>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <Select value={code} options={callingCodeOptions} onChange={setCode} style={{ flex: 1 }} />
            <Input value={national} onChange={(event) => setNational(digitsOnly(event.target.value))} placeholder="Phone number" style={{ flex: 1.4 }} />
          </div>
          {e164 ? <Typography.Text style={{ color: OLIVEEB.blue, fontSize: 12, fontWeight: 800 }}>Will save as: {e164}</Typography.Text> : null}
          <Space style={{ justifyContent: "flex-end" }}>
            <Button type="text" onClick={() => setEditing(false)} icon={<OlitaskHeaderIcon name="close-circle" size={24} />} style={{ color: OLIVEEB.red, fontWeight: 850 }}>
              Cancel
            </Button>
            <Button
              type="text"
              loading={loading}
              disabled={!e164}
              onClick={async () => {
                await onSave(e164);
                setEditing(false);
              }}
              icon={<OlitaskHeaderIcon name="checkmark-circle" size={24} />}
              style={{ color: e164 ? OLIVEEB.green : "#9AA0A6", fontWeight: 850 }}
            >
              Save Phone
            </Button>
          </Space>
        </div>
      )}
    </MobileFieldRow>
  );
};

const GenderFieldRow = ({ value, otherGender, required, missing, loading, onSave, editable = true }) => {
  const [editing, setEditing] = useState(false);
  const [genderDraft, setGenderDraft] = useState(value || "");
  const [otherDraft, setOtherDraft] = useState(otherGender || "");

  useEffect(() => {
    if (!editing) {
      setGenderDraft(value || "");
      setOtherDraft(otherGender || "");
    }
  }, [editing, otherGender, value]);

  const display = value === "OTHER" ? otherGender || "Other" : genderLabel(value);
  const canSave = genderDraft && (genderDraft !== "OTHER" || otherDraft.trim());

  return (
    <MobileFieldRow label="Gender" value={display} required={required} missing={missing} editable={false}>
      {!editing ? (
        <>
          <div style={{ color: OLIVEEB.text, fontSize: 15, lineHeight: "21px", fontWeight: 800 }}>
            {display}
          </div>
          {editable ? (
            <Button type="text" onClick={() => setEditing(true)} icon={<OlitaskHeaderIcon name="pencil" size={18} />} style={{ marginTop: 8, paddingLeft: 0, color: OLIVEEB.greenDark, fontWeight: 850 }}>
              Edit Gender
            </Button>
          ) : null}
        </>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          <div className="d-flex flex-wrap gap-2">
            {profileGenderOptions.map((option) => {
              const selected = genderDraft === option.value;
              return (
                <Button
                  key={option.value}
                  onClick={() => {
                    setGenderDraft(option.value);
                    if (option.value !== "OTHER") setOtherDraft("");
                  }}
                  style={{
                    borderRadius: 999,
                    borderColor: selected ? "#A6F4C5" : OLIVEEB.border,
                    background: selected ? "#ECFDF3" : "#fff",
                    color: selected ? OLIVEEB.greenDark : OLIVEEB.text,
                    fontWeight: 850,
                  }}
                >
                  {option.label}
                </Button>
              );
            })}
          </div>
          {genderDraft === "OTHER" ? (
            <Input value={otherDraft} onChange={(event) => setOtherDraft(event.target.value)} placeholder="Please specify" />
          ) : null}
          <Space style={{ justifyContent: "flex-end" }}>
            <Button type="text" onClick={() => setEditing(false)} icon={<OlitaskHeaderIcon name="close-circle" size={24} />} style={{ color: OLIVEEB.red, fontWeight: 850 }}>
              Cancel
            </Button>
            <Button
              type="text"
              loading={loading}
              disabled={!canSave}
              onClick={async () => {
                await onSave(genderDraft, otherDraft.trim());
                setEditing(false);
              }}
              icon={<OlitaskHeaderIcon name="checkmark-circle" size={24} />}
              style={{ color: canSave ? OLIVEEB.green : "#9AA0A6", fontWeight: 850 }}
            >
              Save Gender
            </Button>
          </Space>
        </div>
      )}
    </MobileFieldRow>
  );
};

const SsnFieldRow = ({ value, loading, onSave, editable = true }) => {
  const [editing, setEditing] = useState(false);
  const [ssn, setSsn] = useState("");
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!editing) {
      setSsn(digitsOnly(value));
      setRevealed(false);
    }
  }, [editing, value]);

  const valid = isValidSsn(ssn);
  const hasSsn = Boolean(digitsOnly(value));
  const displayValue = revealed ? formatSsn(value) : maskSsn(value);

  return (
    <MobileFieldRow label="SSN" value={displayValue} editable={false}>
      {!editing ? (
        <>
          <div className="d-flex align-items-center gap-2">
            <div style={{ color: OLIVEEB.text, fontSize: 15, lineHeight: "21px", fontWeight: 800 }}>
              {displayValue}
            </div>
            {hasSsn ? (
              <Button
                type="text"
                aria-label={revealed ? "Hide SSN" : "Show SSN"}
                title={revealed ? "Hide SSN" : "Show SSN"}
                onClick={() => setRevealed((current) => !current)}
                icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                style={{ width: 30, height: 30, padding: 0, color: OLIVEEB.blue }}
              />
            ) : null}
          </div>
          {editable ? (
            <Space wrap style={{ marginTop: 8 }}>
              <Button
                type="text"
                onClick={() => {
                  setRevealed(false);
                  setEditing(true);
                }}
                icon={<OlitaskHeaderIcon name="pencil" size={18} />}
                style={{ paddingLeft: 0, color: OLIVEEB.greenDark, fontWeight: 850 }}
              >
                Edit SSN
              </Button>
            </Space>
          ) : null}
        </>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          <Input
            value={formatSsn(ssn)}
            onChange={(event) => setSsn(digitsOnly(event.target.value).slice(0, 9))}
            placeholder="123-45-6789"
          />
          <Space style={{ justifyContent: "flex-end" }}>
            <Button type="text" onClick={() => setEditing(false)} icon={<OlitaskHeaderIcon name="close-circle" size={24} />} style={{ color: OLIVEEB.red, fontWeight: 850 }}>
              Cancel
            </Button>
            <Button
              type="text"
              loading={loading}
              disabled={!valid}
              onClick={async () => {
                await onSave(digitsOnly(ssn));
                setRevealed(false);
                setEditing(false);
              }}
              icon={<OlitaskHeaderIcon name="checkmark-circle" size={24} />}
              style={{ color: valid ? OLIVEEB.green : "#9AA0A6", fontWeight: 850 }}
            >
              Save SSN
            </Button>
          </Space>
        </div>
      )}
    </MobileFieldRow>
  );
};

const DateOfBirthFieldRow = ({ value, required, missing, loading, onSave, editable = true }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(cleanValue(value));
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!editing) {
      setDraft(cleanValue(value));
      setRevealed(false);
    }
  }, [editing, value]);

  const hasValue = Boolean(cleanValue(value));
  const displayValue = revealed ? cleanValue(value) : maskDateOfBirth(value);
  const dateValue = draft && dayjs(draft).isValid() ? dayjs(draft) : null;

  const save = async () => {
    if (!onSave) return;
    await onSave(draft);
    setRevealed(false);
    setEditing(false);
  };

  return (
    <MobileFieldRow
      label="Date of Birth (YYYY-MM-DD)"
      value={displayValue}
      required={required}
      missing={missing}
      editable={false}
    >
      {!editing ? (
        <>
          <div className="d-flex align-items-center gap-2">
            <div style={{ color: OLIVEEB.text, fontSize: 15, lineHeight: "21px", fontWeight: 800 }}>
              {displayValue}
            </div>
            {hasValue ? (
              <Button
                type="text"
                aria-label={revealed ? "Hide date of birth" : "Show date of birth"}
                title={revealed ? "Hide date of birth" : "Show date of birth"}
                onClick={() => setRevealed((current) => !current)}
                icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                style={{ width: 30, height: 30, padding: 0, color: OLIVEEB.blue }}
              />
            ) : null}
          </div>
          {editable ? (
            <Button
              type="text"
              onClick={() => {
                setRevealed(false);
                setEditing(true);
              }}
              icon={<OlitaskHeaderIcon name="pencil" size={18} />}
              style={{ marginTop: 8, paddingLeft: 0, color: OLIVEEB.greenDark, fontWeight: 850 }}
            >
              Edit Date of Birth
            </Button>
          ) : null}
        </>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          <DatePicker
            value={dateValue}
            format="YYYY-MM-DD"
            placeholder="YYYY-MM-DD"
            onChange={(_, dateString) => setDraft(dateString || "")}
            disabledDate={(current) => current && current.isAfter(dayjs().subtract(18, "year").endOf("day"))}
            style={{ width: "100%", height: 42, borderRadius: 14 }}
          />
          <Space style={{ justifyContent: "flex-end" }}>
            <Button type="text" onClick={() => setEditing(false)} icon={<OlitaskHeaderIcon name="close-circle" size={24} />} style={{ color: OLIVEEB.red, fontWeight: 850 }}>
              Cancel
            </Button>
            <Button
              type="text"
              loading={loading}
              onClick={save}
              icon={<OlitaskHeaderIcon name="checkmark-circle" size={24} />}
              style={{ color: OLIVEEB.green, fontWeight: 850 }}
            >
              Save Date of Birth
            </Button>
          </Space>
        </div>
      )}
    </MobileFieldRow>
  );
};

const AddressEditor = ({
  profile,
  googleMapsApiKey,
  saving,
  onSave,
  editable = true,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => addressDraftFromProfile(profile));
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (!editing) {
      setDraft(addressDraftFromProfile(profile));
      setSearchValue("");
    }
  }, [editing, profile]);

  const updateDraft = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const beginEdit = () => {
    setDraft(addressDraftFromProfile(profile));
    setEditing(true);
  };

  const handlePlace = async ({ details, prediction }) => {
    const next = locationFieldsFromPlaceDetails(details);
    const formatted = details?.formatted_address || prediction?.description || searchValue;
    const street = next.street || formatted || "";
    setDraft({
      street,
      city: next.city || "",
      state: next.state || "",
      zip: next.zipCode || "",
      country: next.country || "USA",
    });
    setSearchValue(formatted);
  };

  const handleSave = async () => {
    const payload = normalizeAddressInput(draft);
    if (!payload.street || !payload.city || !payload.country) {
      toast.error("Street, City, and Country are required.");
      return;
    }
    const saved = await onSave(draft);
    if (saved) setEditing(false);
  };

  if (!editing) {
    return (
      <MobileFieldRow
        label="Address"
        value={addressString(profile)}
        required
        missing={!hasAddress(profile)}
        editable={false}
      >
        <div style={{ color: OLIVEEB.text, fontSize: 15, lineHeight: "21px", fontWeight: 800, overflowWrap: "anywhere" }}>
          {addressString(profile)}
        </div>
        {editable ? (
          <Button
            type="text"
            onClick={beginEdit}
            icon={<OlitaskHeaderIcon name="pencil" size={18} />}
            style={{ marginTop: 8, paddingLeft: 0, color: OLIVEEB.greenDark, fontWeight: 850 }}
          >
            Edit Address
          </Button>
        ) : null}
      </MobileFieldRow>
    );
  }

  return (
    <div style={{ borderTop: "1px solid #EEF2F6", padding: "14px 0" }}>
      <div className="d-flex align-items-center flex-wrap gap-2" style={{ marginBottom: 8 }}>
        <span style={{ color: OLIVEEB.subtext, fontSize: 12, fontWeight: 900, letterSpacing: ".02em", textTransform: "uppercase" }}>
          Address
        </span>
        <Tag color="blue" style={{ borderRadius: 999, marginInlineEnd: 0, fontWeight: 800 }}>
          Required
        </Tag>
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <Button
          type="default"
          icon={<OlitaskHeaderIcon name="search-outline" size={18} />}
          onClick={() => document.querySelector("[data-profile-address-search='true'] input")?.focus?.()}
          style={{ borderRadius: 12, color: OLIVEEB.blue, borderColor: "#D1E9FF", fontWeight: 850, justifySelf: "start" }}
        >
          Search Address
        </Button>
        <div data-profile-address-search="true">
          <GooglePlacesAutocompleteInput
            googleMapsApiKey={googleMapsApiKey}
            placeholder="Search address"
            value={searchValue}
            onChange={setSearchValue}
            types={["address"]}
            onResolvePlace={handlePlace}
          />
        </div>

        <Input value={draft.street} onChange={(event) => updateDraft("street", event.target.value)} placeholder="Street Address" />
        <Row gutter={[8, 8]}>
          <Col xs={24} md={14}>
            <Input value={draft.city} onChange={(event) => updateDraft("city", event.target.value)} placeholder="City" />
          </Col>
          <Col xs={12} md={5}>
            <Input value={draft.state} onChange={(event) => updateDraft("state", event.target.value)} placeholder="State" />
          </Col>
          <Col xs={12} md={5}>
            <Input value={draft.zip} onChange={(event) => updateDraft("zip", digitsOnly(event.target.value))} placeholder="ZIP" />
          </Col>
        </Row>
        <Input value={draft.country} onChange={(event) => updateDraft("country", event.target.value)} placeholder="Country" />

        <Space style={{ justifyContent: "flex-end" }}>
          <Button
            type="text"
            onClick={() => setEditing(false)}
            icon={<OlitaskHeaderIcon name="close-circle" size={24} />}
            style={{ color: OLIVEEB.red, fontWeight: 850 }}
          >
            Cancel
          </Button>
          <Button
            type="text"
            loading={saving}
            onClick={handleSave}
            icon={<OlitaskHeaderIcon name="checkmark-circle" size={24} />}
            style={{ color: OLIVEEB.green, fontWeight: 850 }}
          >
            Save Address
          </Button>
        </Space>
      </div>
    </div>
  );
};

const identityDocumentOptions = [
  { label: "Passport", value: "PASSPORT" },
  { label: "Driver's License", value: "DRIVERS_LICENSE" },
  { label: "Government ID", value: "GOVERNMENT_ID" },
];

const MAX_PICK_SIZE_MB = 15;
const TARGET_SIZE_BYTES = 800 * 1024;
const HARD_MAX_SIZE_BYTES = 1024 * 1024;
const QUALITY_STEPS = [0.82, 0.74, 0.66, 0.58, 0.5, 0.42];
const MAX_LONGEST_SIDE_STEPS = [2000, 1800, 1600, 1400, 1200];
const HEIC_CONVERTER_URL = "https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js";
const HEIC_TO_CONVERTER_URL = "https://cdn.jsdelivr.net/npm/heic-to@1.5.2/dist/iife/heic-to.js";

let heicConverterPromise;
let heicToConverterPromise;

const formatFileMB = (bytes) => (bytes ? `${(bytes / (1024 * 1024)).toFixed(2)}MB` : "-");

const withTimeout = (promise, timeoutMs, message) => {
  let timeoutRef;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutRef = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutRef) clearTimeout(timeoutRef);
  });
};

const isHeicFile = (file = {}) => {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  return type.includes("heic") || type.includes("heif") || name.endsWith(".heic") || name.endsWith(".heif");
};

const toJpegFilename = (filename) => {
  const base = String(filename || "upload")
    .replace(/\?.*$/, "")
    .replace(/\.[^.]+$/, "");
  return `${base || "upload"}.jpg`;
};

const loadHeicConverter = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("HEIC conversion is not available."));
  }
  if (window.heic2any) return Promise.resolve(window.heic2any);
  if (heicConverterPromise) return heicConverterPromise;

  heicConverterPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = HEIC_CONVERTER_URL;
    script.async = true;
    script.onload = () => {
      if (window.heic2any) resolve(window.heic2any);
      else reject(new Error("HEIC converter did not load."));
    };
    script.onerror = () => reject(new Error("Could not load HEIC converter."));
    document.head.appendChild(script);
  });

  heicConverterPromise = heicConverterPromise.catch((error) => {
    heicConverterPromise = null;
    throw error;
  });

  return heicConverterPromise;
};

const loadHeicToConverter = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("HEIC conversion is not available."));
  }
  if (window.HeicTo) return Promise.resolve(window.HeicTo);
  if (heicToConverterPromise) return heicToConverterPromise;

  heicToConverterPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = HEIC_TO_CONVERTER_URL;
    script.async = true;
    script.onload = () => {
      if (window.HeicTo) resolve(window.HeicTo);
      else reject(new Error("HEIC fallback converter did not load."));
    };
    script.onerror = () => reject(new Error("Could not load HEIC fallback converter."));
    document.head.appendChild(script);
  });

  heicToConverterPromise = heicToConverterPromise.catch((error) => {
    heicToConverterPromise = null;
    throw error;
  });

  return heicToConverterPromise;
};

const convertHeicToJpegFile = async (file, onDetail) => {
  onDetail?.("Preparing document...");
  let converted;
  try {
    const heic2any = await loadHeicConverter();
    converted = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.88,
    });
  } catch {
    onDetail?.("Preparing document...");
    const heicTo = await loadHeicToConverter();
    converted = await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 0.88,
    });
  }
  const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
  if (!(jpegBlob instanceof Blob)) {
    throw new Error("Could not convert HEIC/HEIF document to JPEG.");
  }
  const jpegName = toJpegFilename(file.name || `doc-${Date.now()}.jpg`);
  return new File([jpegBlob], jpegName, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};

const createDocumentPreviewUrl = (file) => {
  if (!(file instanceof Blob)) return "";
  return URL.createObjectURL(file);
};

const loadImageFile = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read the selected photo."));
    };
    image.src = url;
  });

const canvasBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Could not compress the selected photo."));
      },
      "image/jpeg",
      quality,
    );
  });

const getScaledDimensions = (width, height, maxLongestSide) => {
  const longestSide = Math.max(width, height);
  if (!width || !height || longestSide <= maxLongestSide) return { width, height };
  const scale = maxLongestSide / longestSide;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const optimizeJpegReadablePhoto = async (file, onDetail) => {
  const image = await loadImageFile(file);
  let currentWidth = image.naturalWidth || image.width;
  let currentHeight = image.naturalHeight || image.height;
  let currentBlob = file;

  onDetail?.(`Checking size... (${formatFileMB(currentBlob.size)})`);

  for (const maxLongestSide of MAX_LONGEST_SIDE_STEPS) {
    const nextDimensions = getScaledDimensions(currentWidth, currentHeight, maxLongestSide);
    const canvas = document.createElement("canvas");
    canvas.width = nextDimensions.width;
    canvas.height = nextDimensions.height;
    const context = canvas.getContext("2d");
    context.drawImage(image, 0, 0, nextDimensions.width, nextDimensions.height);

    if (nextDimensions.width !== currentWidth || nextDimensions.height !== currentHeight) {
      onDetail?.(`Resizing... ${nextDimensions.width}x${nextDimensions.height}`);
      currentWidth = nextDimensions.width;
      currentHeight = nextDimensions.height;
    }

    for (const quality of QUALITY_STEPS) {
      if (currentBlob.size <= TARGET_SIZE_BYTES) break;
      onDetail?.(`Compressing... q=${quality.toFixed(2)} - ${formatFileMB(currentBlob.size)}`);
      currentBlob = await canvasBlob(canvas, quality);
    }

    if (currentBlob.size <= TARGET_SIZE_BYTES) break;
  }

  if (currentBlob.size > HARD_MAX_SIZE_BYTES) {
    toast.warning(`Image is still ${formatFileMB(currentBlob.size)}. We reduced it as much as possible while keeping it readable.`);
  } else {
    onDetail?.(`Final size: ${formatFileMB(currentBlob.size)}`);
  }

  return new File([currentBlob], toJpegFilename(file.name || `doc-${Date.now()}.jpg`), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};

const optimizeDocumentPhoto = async (file, onDetail) => {
  if (file.size > MAX_PICK_SIZE_MB * 1024 * 1024) {
    throw new Error(`Please select a photo under ${MAX_PICK_SIZE_MB}MB.`);
  }
  if (!file?.type?.startsWith("image/") && !isHeicFile(file)) return file;

  if (isHeicFile(file)) {
    try {
      onDetail?.("Preparing document...");
      return await optimizeJpegReadablePhoto(file, onDetail);
    } catch {
      try {
        const jpegFile = await convertHeicToJpegFile(file, onDetail);
        return await optimizeJpegReadablePhoto(jpegFile, onDetail);
      } catch (conversionError) {
        const readableError = new Error(
          "Could not convert this HEIC/HEIF photo to JPEG. Please retake it as JPEG or choose a different photo.",
        );
        readableError.cause = conversionError;
        throw readableError;
      }
    }
  }

  return optimizeJpegReadablePhoto(file, onDetail);
};

const IdentityVerificationEditor = ({
  identityVerification,
  submitting,
  submissionStep,
  onSubmit,
  editable = true,
}) => {
  const [documentType, setDocumentType] = useState("");
  const [fileList, setFileList] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showRecommendation, setShowRecommendation] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [processingTitle, setProcessingTitle] = useState("Processing document...");
  const [processingDetail, setProcessingDetail] = useState("Preparing document for upload...");

  const activeTitle = submitting ? "Uploading document..." : processingTitle;
  const activeDetail = submitting ? submissionStep || "Uploading document photos..." : processingDetail;
  const canSubmit = documentType && fileList.length >= 2 && !submitting;
  const verification = identityVerification && typeof identityVerification === "object" ? identityVerification : undefined;
  const hasDocument = Boolean(
    (typeof verification?.idDocumentId === "string" && verification.idDocumentId.trim().length > 0) ||
    (Array.isArray(verification?.idDocumentIds) && verification.idDocumentIds.some((id) => !!String(id || "").trim())) ||
    (typeof verification?.idDocumentIds === "string" && verification.idDocumentIds.trim().length > 0) ||
    verification?.uploadedOn
  );
  const documentTypeValue =
    verification?.documentType ||
    verification?.identificationDocumentType ||
    verification?.identityDocumentType ||
    verification?.document?.type ||
    verification?.identityDocument?.type ||
    null;
  const verified = isIdentityVerificationVerified(verification);
  const prettify = (value) => {
    if (!value) return "N/A";
    return String(value)
      .toLowerCase()
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const submit = async () => {
    const submitted = await onSubmit(documentType, fileList.map((item) => item.originFileObj || item).filter(Boolean));
    if (submitted) {
      setDocumentType("");
      setFileList([]);
      setDrawerOpen(false);
    }
  };
  const pageLabel = (index) => {
    if (index === 0) return "Front / First Page";
    if (index === 1) return "Back / Second Page";
    return `Extra Page ${index + 1}`;
  };
  const nextPageLabel = fileList.length === 0 ? "Front / First Page" : fileList.length === 1 ? "Back / Second Page" : `Extra Page ${fileList.length + 1}`;
  const drawerWidth = typeof window !== "undefined" && window.innerWidth <= 560 ? "100vw" : 520;

  const normalizeUploadFile = async (file, title = "Processing document...") => {
    const rawFile = file?.originFileObj || file;
    if (!rawFile) return null;
    const shouldConvertHeic = isHeicFile(rawFile);
    setProcessingTitle(shouldConvertHeic ? "Processing document..." : title);
    setProcessingDetail(shouldConvertHeic ? "Preparing document for upload..." : "Preparing document for upload...");
    setProcessing(true);
    try {
      const optimizedFile = await withTimeout(
        optimizeDocumentPhoto(rawFile, setProcessingDetail),
        30000,
        "Photo processing timed out. Please try a smaller or clearer image.",
      );
      let previewUrl = "";
      try {
        previewUrl = await createDocumentPreviewUrl(optimizedFile);
      } catch (previewError) {
        toast.warning(previewError?.message || "Selected file attached, but preview is unavailable.");
      }
      return {
        uid: `${Date.now()}-${optimizedFile.name}`,
        name: optimizedFile.name || rawFile.name || `doc-${Date.now()}.jpg`,
        type: optimizedFile.type || rawFile.type || "",
        size: optimizedFile.size,
        originFileObj: optimizedFile,
        previewUrl,
      };
    } finally {
      setProcessing(false);
    }
  };

  const appendFiles = async (nextFiles = [], title = "Processing document...") => {
    const normalized = [];
    for (const file of nextFiles.filter(Boolean)) {
      try {
        const nextFile = await normalizeUploadFile(file, title);
        if (nextFile) normalized.push(nextFile);
      } catch (error) {
        Modal.error({
          title: "Could not attach photo",
          content: error?.message || "Failed to process selected photo.",
          okText: "OK",
        });
      }
    }
    if (normalized.length) setFileList((current) => [...current, ...normalized]);
  };
  const removeFileAt = (index) => {
    setFileList((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };
  const replaceFileAt = async (index, file, title = "Processing document...") => {
    if (!file) return;
    try {
      const nextFile = await normalizeUploadFile(file, title);
      if (!nextFile) return;
      setFileList((current) => current.map((item, itemIndex) => (itemIndex === index ? nextFile : item)));
      setPreviewFile(null);
    } catch (error) {
      Modal.error({
        title: "Could not update photo",
        content: error?.message || "Failed to update selected photo.",
        okText: "OK",
      });
    }
  };
  const filePreviewUrl = (file) => {
    if (file?.previewUrl || file?.thumbUrl || file?.url) return file.previewUrl || file.thumbUrl || file.url;
    const rawFile = file?.originFileObj || file;
    if (isHeicFile(file) || isHeicFile(rawFile)) return null;
    return rawFile instanceof Blob ? URL.createObjectURL(rawFile) : null;
  };

  return (
    <>
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          border: "1px solid #EAF4FF",
          borderRadius: 18,
          padding: 14,
          background: "#F7FAF7",
          marginBottom: 24,
          boxShadow: "0 4px 10px rgba(35, 133, 219, 0.12)",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: -36,
            right: -24,
            width: 120,
            height: 120,
            borderRadius: 999,
            background: "rgba(35,133,219,0.14)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            bottom: -44,
            left: -30,
            width: 136,
            height: 136,
            borderRadius: 999,
            background: "rgba(39,174,48,0.12)",
          }}
        />

        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="d-flex align-items-center justify-content-between gap-2" style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: "#0e5604" }}>
              Identity Verification
            </div>
            <div
              style={{
                border: `1px solid ${verified ? "#27ae30" : "#d90101"}`,
                borderRadius: 999,
                padding: "3px 8px",
                background: verified ? "#F6FFF6" : "#FDECEC",
                color: verified ? "#27ae30" : "#5b0d03",
                fontWeight: 700,
                fontSize: 10.5,
              }}
            >
              {verified ? "Verified" : "Not Verified"}
            </div>
          </div>

          <div
            style={{
              padding: "8px 10px",
              border: "1px solid #E6EFE7",
              borderRadius: 12,
              background: "#FFFFFF",
              marginBottom: 7,
            }}
          >
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ marginBottom: 4 }}>
              <div style={{ color: "#6A7A6B", fontSize: 12.5, fontWeight: 700 }}>
                Identity Verification Document
              </div>
            </div>
            <div style={{ color: hasDocument ? "#27ae30" : "#5b0d03", fontSize: 12.5, fontWeight: 600 }}>
              {hasDocument ? "Uploaded" : "Not uploaded"}
            </div>
          </div>

          <div
            style={{
              padding: "8px 10px",
              border: "1px solid #E6EFE7",
              borderRadius: 12,
              background: "#FFFFFF",
              marginBottom: 7,
            }}
          >
            <div className="d-flex align-items-center">
              <div style={{ color: "#6A7A6B", fontSize: 12.5, fontWeight: 700, width: 120 }}>
                Document Type
              </div>
              <div style={{ color: "#062701", fontSize: 12.5, fontWeight: 600, flex: 1 }}>
                {prettify(documentTypeValue)}
              </div>
            </div>
          </div>

          <div
            style={{
              padding: "8px 10px",
              border: "1px solid #E6EFE7",
              borderRadius: 12,
              background: "#FFFFFF",
              marginBottom: 7,
            }}
          >
            <div className="d-flex align-items-center">
              <div style={{ color: "#6A7A6B", fontSize: 12.5, fontWeight: 700, width: 120 }}>
                Uploaded On
              </div>
              <div style={{ color: "#062701", fontSize: 12.5, fontWeight: 600, flex: 1 }}>
                {verification?.uploadedOn ? new Date(verification.uploadedOn).toLocaleString() : "N/A"}
              </div>
            </div>
          </div>

          {verified ? (
            <div
              style={{
                padding: "8px 10px",
                border: "1px solid #E6EFE7",
                borderRadius: 12,
                background: "#FFFFFF",
                marginBottom: 7,
              }}
            >
              <div className="d-flex align-items-center">
                <div style={{ color: "#6A7A6B", fontSize: 12.5, fontWeight: 700, width: 120 }}>
                  Verified On
                </div>
                <div style={{ color: "#062701", fontSize: 12.5, fontWeight: 600, flex: 1 }}>
                  {verification?.verifiedOn || verification?.verifiedAt
                    ? new Date(verification.verifiedOn || verification.verifiedAt).toLocaleString()
                    : "N/A"}
                </div>
              </div>
            </div>
          ) : null}

          {editable ? (
            <Button
              type="primary"
              onClick={() => setDrawerOpen(true)}
              icon={<OlitaskHeaderIcon name="create-outline" size={16} />}
              style={{
                width: "100%",
                marginTop: 12,
                paddingBlock: 11,
                height: "auto",
                borderRadius: 10,
                background: "#2385DB",
                borderColor: "#f8c10b",
                boxShadow: "0 3px 8px rgba(255, 255, 0, 0.16)",
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              Update Document
            </Button>
          ) : null}
        </div>
      </div>
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Identity Verification"
        placement="right"
        width={drawerWidth}
        bodyStyle={{ padding: 14, background: "#F6FBF9" }}
        destroyOnClose={false}
      >
        {processing || submitting ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              background: "rgba(0,0,0,0.35)",
              display: "grid",
              placeItems: "center",
              padding: 24,
            }}
          >
            <div style={{ background: "#FFFFFF", padding: 24, borderRadius: 20, width: "min(80%, 340px)" }}>
              <div style={{ display: "inline-flex", background: "#E7F6EF", color: "#0e5604", borderRadius: 8, padding: "4px 8px", fontWeight: 900, fontSize: 10 }}>
                Olitask
              </div>
              <div style={{ color: "#0e5604", fontWeight: 900, fontSize: 18, marginTop: 8 }}>
                {activeTitle}
              </div>
              <div style={{ color: "#27ae30", marginTop: 4 }}>
                {activeDetail}
              </div>
              <Spin style={{ marginTop: 14 }} />
            </div>
          </div>
        ) : null}
        <div style={{ display: "grid", gap: 14 }}>
          <div
            style={{
              border: "1px solid #E6EFE7",
              borderRadius: 18,
              padding: 16,
              background: "linear-gradient(135deg, #E7F6EF 0%, #EAF4FF 55%, #FFFFFF 100%)",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                display: "grid",
                placeItems: "center",
                background: "#E7F6EF",
                border: "1px solid #E6EFE7",
                color: "#0e5604",
                marginBottom: 10,
              }}
            >
              <OlitaskHeaderIcon name="shield-checkmark-outline" size={22} />
            </div>
            <div style={{ color: "#0e5604", fontSize: 18, fontWeight: 900 }}>
              Upload Document Photos
            </div>
            <div style={{ color: "#5b0d03", marginTop: 4, lineHeight: "18px", fontSize: 12.5 }}>
              Ensure all details are clearly visible.
            </div>
          </div>

          <div>
            <div style={{ color: "#27ae30", fontSize: 13, fontWeight: 900, marginBottom: 6 }}>
              Document Type
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
              {identityDocumentOptions.map((option) => {
                const selected = documentType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setDocumentType(option.value)}
                    style={{
                      minHeight: 37,
                      padding: "9px 8px",
                      borderRadius: 999,
                      border: `1px solid ${selected ? "#A6F4C5" : "#E6EFE7"}`,
                      background: selected ? "#ECFDF3" : "#FFFFFF",
                      color: selected ? "#0e5604" : "#062701",
                      fontSize: 12,
                      fontWeight: 800,
                      textAlign: "center",
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {showRecommendation ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{
                background: "#FFF6D8",
                border: "1px solid #F2D89A",
                borderRadius: 10,
                padding: "8px 10px",
              }}
            >
              <OlitaskHeaderIcon name="flash-outline" size={14} style={{ color: "#9A5200", flex: "0 0 auto" }} />
              <div style={{ flex: 1, color: "#7A3F00", fontSize: 12, fontWeight: 700, lineHeight: "16px" }}>
                Recommended: Use <strong style={{ color: "#9A5200", fontWeight: 900 }}>Driver&apos;s License</strong> for instant and more reliable verification.
              </div>
              <button
                type="button"
                onClick={() => setShowRecommendation(false)}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  border: "1px solid #F2D89A",
                  background: "#FFF9E9",
                  color: "#8A4A00",
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 900,
                }}
              >
                x
              </button>
            </div>
          ) : null}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
            <Upload
              beforeUpload={() => false}
              showUploadList={false}
              accept="image/*,.heic,.heif"
              multiple
              onChange={({ file }) => appendFiles([file], "Processing document...")}
            >
              <button
                type="button"
                style={{
                  width: "100%",
                  border: 0,
                  borderRadius: 18,
                  padding: 12,
                  minHeight: 122,
                  background: "linear-gradient(135deg, #2385DB 0%, #090359 100%)",
                  color: "#FFFFFF",
                  textAlign: "left",
                }}
              >
                <div style={{ width: 28, height: 28, borderRadius: 9, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.16)", marginBottom: 6 }}>
                  <OlitaskHeaderIcon name="images-outline" size={18} />
                </div>
                <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 9.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>
                  Gallery
                </div>
                <div style={{ fontWeight: 900, fontSize: 14 }}>Upload {nextPageLabel}</div>
                <div style={{ marginTop: 2, color: "rgba(255,255,255,0.86)", fontSize: 11, lineHeight: "15px", fontWeight: 600 }}>
                  Use an existing photo
                </div>
              </button>
            </Upload>
            <Upload
              beforeUpload={() => false}
              showUploadList={false}
              accept="image/*,.heic,.heif"
              capture="environment"
              onChange={({ file }) => appendFiles([file], "Processing document...")}
            >
              <button
                type="button"
                style={{
                  width: "100%",
                  border: 0,
                  borderRadius: 18,
                  padding: 12,
                  minHeight: 122,
                  background: "linear-gradient(135deg, #27ae30 0%, #0e5604 100%)",
                  color: "#FFFFFF",
                  textAlign: "left",
                }}
              >
                <div style={{ width: 28, height: 28, borderRadius: 9, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.16)", marginBottom: 6 }}>
                  <OlitaskHeaderIcon name="camera-outline" size={18} />
                </div>
                <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 9.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>
                  Camera
                </div>
                <div style={{ fontWeight: 900, fontSize: 14 }}>Capture {nextPageLabel}</div>
                <div style={{ marginTop: 2, color: "rgba(255,255,255,0.86)", fontSize: 11, lineHeight: "15px", fontWeight: 600 }}>
                  Take a new photo now
                </div>
              </button>
            </Upload>
          </div>

          {fileList.map((file, index) => {
            const previewUrl = filePreviewUrl(file);
            const displayName = file.name || "Selected document";
            return (
              <div
                key={file.uid || `${file.name}-${index}`}
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #E6EFE7",
                  borderRadius: 16,
                  padding: 10,
                }}
              >
                <div className="d-flex align-items-center justify-content-between gap-2" style={{ marginBottom: 8 }}>
                  <div className="d-flex align-items-center gap-2" style={{ color: "#0e5604", fontWeight: 900 }}>
                    <OlitaskHeaderIcon name="document-text-outline" size={15} />
                    {pageLabel(index)}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFileAt(index)}
                    style={{
                      background: "#111111",
                      border: 0,
                      borderRadius: 8,
                      color: "#FFFFFF",
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "5px 8px",
                    }}
                  >
                    Remove x
                  </button>
                </div>
                {previewUrl ? (
                  <button
                    type="button"
                    onClick={() => setPreviewFile({ ...file, previewUrl, index })}
                    style={{
                      border: 0,
                      padding: 0,
                      background: "#F7FAF7",
                      borderRadius: 12,
                      width: "100%",
                      minHeight: 160,
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        placeItems: "center",
                        padding: 18,
                        color: "#475569",
                        fontWeight: 800,
                      }}
                    >
                      {displayName}
                    </div>
                    <img
                      src={previewUrl}
                      alt=""
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                      style={{ position: "relative", width: "100%", height: 160, borderRadius: 12, objectFit: "cover", background: "#F7FAF7" }}
                    />
                  </button>
                ) : (
                  <div style={{ borderRadius: 12, background: "#F7FAF7", padding: 18, color: "#475569", fontWeight: 800 }}>
                    <div>{displayName}</div>
                  </div>
                )}
              </div>
            );
          })}

          <Button
            type="primary"
            disabled={!canSubmit}
            loading={submitting}
            onClick={submit}
            style={{
              background: "#27ae30",
              borderColor: "#27ae30",
              borderRadius: 14,
              height: 48,
              fontWeight: 900,
              fontSize: 16,
              opacity: canSubmit ? 1 : 0.5,
            }}
          >
            Submit Verification
          </Button>
        </div>
      </Drawer>
      <Modal
        open={Boolean(previewFile)}
        onCancel={() => setPreviewFile(null)}
        footer={null}
        centered
        width="min(92vw, 720px)"
        bodyStyle={{ padding: 0, background: "#000000" }}
      >
        {previewFile ? (
          <div style={{ background: "#000000", minHeight: "72vh", display: "grid", gridTemplateRows: "auto minmax(0, 1fr) auto" }}>
            <div className="d-flex align-items-center justify-content-between" style={{ color: "#FFFFFF", fontWeight: 900, padding: 20 }}>
              {pageLabel(previewFile.index)}
              <button
                type="button"
                onClick={() => setPreviewFile(null)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: 0,
                  borderRadius: 999,
                  color: "#FFFFFF",
                  height: 40,
                  width: 40,
                  fontWeight: 900,
                }}
              >
                x
              </button>
            </div>
            <div style={{ display: "grid", placeItems: "center", minHeight: 0, padding: "0 16px" }}>
              <img src={previewFile.previewUrl} alt="" style={{ width: "100%", maxHeight: "64vh", objectFit: "contain" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, padding: 16 }}>
              <Upload
                beforeUpload={() => false}
                showUploadList={false}
                accept="image/*,.heic,.heif"
                onChange={({ file }) => replaceFileAt(previewFile.index, file, "Processing document...")}
              >
                <button
                  type="button"
                  style={{
                    width: "100%",
                    border: 0,
                    borderRadius: 14,
                    padding: 13,
                    background: "#2385DB",
                    color: "#FFFFFF",
                    textAlign: "left",
                  }}
                >
                  <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
                    Gallery
                  </div>
                  <div style={{ color: "#FFFFFF", fontWeight: 900, fontSize: 13 }}>Replace Photo</div>
                </button>
              </Upload>
              <Upload
                beforeUpload={() => false}
                showUploadList={false}
                accept="image/*,.heic,.heif"
                capture="environment"
                onChange={({ file }) => replaceFileAt(previewFile.index, file, "Processing document...")}
              >
                <button
                  type="button"
                  style={{
                    width: "100%",
                    border: 0,
                    borderRadius: 14,
                    padding: 13,
                    background: "#27ae30",
                    color: "#FFFFFF",
                    textAlign: "left",
                  }}
                >
                  <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
                    Camera
                  </div>
                  <div style={{ color: "#FFFFFF", fontWeight: 900, fontSize: 13 }}>Capture Again</div>
                </button>
              </Upload>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
};

const UserProfilePage = ({ userIdOverride = "", embedded = false, readOnly = false, hideSensitiveInfo = false } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const { user: authUser, refreshAuth, careRoles = [], roles = [], userRoles = [] } = useAuth();
  const { selectedWorkspaceUse } = useWorkspace();
  const targetUserId = userIdOverride || userId || authUser?.id;
  const isOwnProfile = !targetUserId || String(targetUserId) === String(authUser?.id);
  const canViewSensitiveProfileInfo = !hideSensitiveInfo && (
    isOwnProfile || hasCareOwnerOrAdminRole([...careRoles, ...roles, ...userRoles])
  );
  const canEnterEditMode = !readOnly;
  const urlRequestsEditMode = ["1", "true", "yes"].includes(String(new URLSearchParams(location.search).get("edit") || "").toLowerCase());
  const [profileEditMode, setProfileEditMode] = useState(() => canEnterEditMode && urlRequestsEditMode);
  const canEditProfile = canEnterEditMode && profileEditMode;
  const canEditSensitiveProfileInfo = canEditProfile && canViewSensitiveProfileInfo;
  const [form] = Form.useForm();
  const { getUserById, patchUserProperties, patchProfileProperties } = useUsers(
    0,
    20,
    null,
    true,
    { autoFetch: false },
  );
  const { uploadFile, getFileMetadata, getFileSignedUrl, submitIdentityVerification, uploading } = useFileUpload();
  const { appConfig } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";

  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState({});
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [submittingIdentity, setSubmittingIdentity] = useState(false);
  const [identitySubmissionStep, setIdentitySubmissionStep] = useState("");
  const [profile, setProfile] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [securityPhoto, setSecurityPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [verificationModal, setVerificationModal] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationLoading, setVerificationLoading] = useState({});
  const showSecurityTab = location.state?.activeTab === "security";

  useEffect(() => {
    if (!canEnterEditMode) {
      setProfileEditMode(false);
      return;
    }
    if (urlRequestsEditMode) setProfileEditMode(true);
  }, [canEnterEditMode, urlRequestsEditMode]);

  const loadProfile = useCallback(async () => {
    if (!targetUserId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const fresh = await getUserById(targetUserId);
      setProfile(fresh || null);
      form.setFieldsValue({
        firstName: cleanValue(fresh?.firstName),
        middleName: cleanValue(fresh?.middleName),
        lastName: cleanValue(fresh?.lastName),
        username: cleanValue(fresh?.username),
        email: cleanValue(fresh?.email),
        phone: cleanValue(fresh?.phone),
        gender: cleanValue(fresh?.gender),
        status: cleanValue(fresh?.status || "ACTIVE"),
      });

      const nextProfilePhoto = await readPhotoUrl(fresh?.profilePhoto, getFileMetadata, getFileSignedUrl);
      const nextSecurityPhoto = await readPhotoUrl(fresh?.securityPhoto, getFileMetadata, getFileSignedUrl);
      setProfilePhoto(cacheBustPhotoUrl(nextProfilePhoto));
      setSecurityPhoto(cacheBustPhotoUrl(nextSecurityPhoto));
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [form, getFileMetadata, getFileSignedUrl, getUserById, targetUserId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const saveField = useCallback(
    async (fieldName, nextValue) => {
      if (!profile?.id) return;

      const normalized = cleanValue(nextValue).trim();
      const currentValue = cleanValue(profile?.[fieldName]).trim();
      if (normalized === currentValue) return;
      if (fieldName === "birthDate" && !isAtLeast18(normalized)) {
        toast.error("Date of birth can only be updated for users who are 18 years or older.");
        return;
      }

      setSavingField((prev) => ({ ...prev, [fieldName]: true }));
      try {
        const identityFields = ["username", "email", "phone", "firstName", "middleName", "lastName", "status"];
        const payload = [{ name: fieldName, value: normalized }];
        if (identityFields.includes(fieldName)) {
          await patchUserProperties(profile.id, payload);
        } else {
          await patchProfileProperties(profile.id, payload);
        }

        setProfile((prev) => ({ ...prev, [fieldName]: normalized }));

        if (isOwnProfile) {
          await refreshAuth();
        }

        toast.success(`${fieldName} updated successfully`);
      } catch (error) {
        toast.error(error?.response?.data?.message || error.message || `Failed to update ${fieldName}`);
      } finally {
        setSavingField((prev) => ({ ...prev, [fieldName]: false }));
      }
    },
    [isOwnProfile, patchProfileProperties, patchUserProperties, profile, refreshAuth],
  );

  const saveGender = useCallback(
    async (genderValue, otherGenderValue = "") => {
      if (!profile?.id) return;
      if (!genderValue) {
        toast.error("Please select a gender.");
        return;
      }
      if (genderValue === "OTHER" && !otherGenderValue.trim()) {
        toast.error("Please specify your gender when selecting Other.");
        return;
      }

      setSavingField((prev) => ({ ...prev, gender: true }));
      try {
        const payload = [{ name: "gender", value: genderValue }];
        if (genderValue === "OTHER") {
          payload.push({ name: "otherGender", value: otherGenderValue.trim() });
        } else if (profile?.otherGender) {
          payload.push({ name: "otherGender", value: "" });
        }
        await patchProfileProperties(profile.id, payload);
        setProfile((prev) => ({
          ...prev,
          gender: genderValue,
          otherGender: genderValue === "OTHER" ? otherGenderValue.trim() : "",
        }));
        if (isOwnProfile) await refreshAuth();
        toast.success("Gender updated successfully");
      } catch (error) {
        toast.error(error?.response?.data?.message || error.message || "Failed to update gender");
      } finally {
        setSavingField((prev) => ({ ...prev, gender: false }));
      }
    },
    [isOwnProfile, patchProfileProperties, profile, refreshAuth],
  );

  const savePhoto = useCallback(
    async (fieldName, file) => {
      if (!profile?.id || !file) return;

      const previewUrl = URL.createObjectURL(file);
      if (fieldName === "profilePhoto") setProfilePhoto(previewUrl);
      if (fieldName === "securityPhoto") setSecurityPhoto(previewUrl);

      try {
        const upload = await uploadFile(profile.id, file, {
          purpose: "USER_PROFILE",
          fileType: "PHOTO",
        });
        const fileId = upload?.fileId || upload?.file?.id || upload?.file?.fileId || null;
        if (!fileId) throw new Error("No file id returned from upload");

        await patchProfileProperties(profile.id, [{ name: fieldName, value: fileId }]);

        const resolved = cacheBustPhotoUrl(await readPhotoUrl(fileId, getFileMetadata, getFileSignedUrl));
        const nextPreview = resolved || previewUrl;
        if (fieldName === "profilePhoto") setProfilePhoto(nextPreview);
        if (fieldName === "securityPhoto") setSecurityPhoto(nextPreview);

        setProfile((prev) => ({
          ...prev,
          [fieldName]: fileId,
          [`${fieldName}At`]: new Date().toISOString(),
        }));
        if (isOwnProfile) {
          await refreshAuth();
        }
        toast.success("Profile photo updated");
      } catch (error) {
        toast.error(error?.response?.data?.message || error.message || "Failed to update photo");
      } finally {
        // Keep the preview URL alive until the next upload or navigation.
      }
    },
    [getFileMetadata, getFileSignedUrl, isOwnProfile, patchProfileProperties, profile?.id, refreshAuth, uploadFile],
  );

  const handlePasswordSubmit = async (values) => {
    setSavingPassword(true);
    try {
      await apiClient.put("/identity-service/api/v1/auth/password", {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success("Password updated successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to update password");
    } finally {
      setSavingPassword(false);
    }
  };

  const saveAddress = useCallback(
    async (draft) => {
      if (!profile?.id) return;
      const payload = normalizeAddressInput(draft);
      if (!payload.street || !payload.city || !payload.country) {
        toast.error("Street, City, and Country are required.");
        return false;
      }

      setSavingAddress(true);
      try {
        const response = await apiClient.post(
          `/profile-service/api/v1/user-profiles/user/${profile.id}/address`,
          payload,
        );
        const updatedProfile = response?.data && typeof response.data === "object"
          ? response.data
          : null;
        setProfile((prev) => ({
          ...prev,
          ...(updatedProfile || {}),
          permanentAddress: updatedProfile?.permanentAddress || updatedProfile?.residentialAddress || payload,
        }));
        await loadProfile();
        if (isOwnProfile) await refreshAuth();
        toast.success("Address updated");
        return true;
      } catch (error) {
        toast.error(error?.response?.data?.message || error.message || "Failed to update address");
        return false;
      } finally {
        setSavingAddress(false);
      }
    },
    [isOwnProfile, loadProfile, profile?.id, refreshAuth],
  );

  const emailValue = cleanValue(profile?.email || profile?.contactDetails?.personalEmail || profile?.contactDetails?.email);
  const phoneValue = cleanValue(profile?.phone || profile?.contactDetails?.mobilePhone);

  const submitIdentityDocuments = useCallback(
    async (documentType, files) => {
      if (!profile?.id) return;
      if (!documentType) {
        toast.error("Document type is required.");
        return false;
      }
      if (!files?.length) {
        toast.error("Upload at least one identity document.");
        return false;
      }

      setSubmittingIdentity(true);
      setIdentitySubmissionStep("Uploading document photos...");
      try {
        const uploadedFileIds = [];
        for (let index = 0; index < files.length; index += 1) {
          const file = files[index];
          setIdentitySubmissionStep(`Uploading document photo ${index + 1} of ${files.length}...`);
          const upload = await uploadFile(profile.id, file, {
            purpose: "USER_PROFILE",
            fileType: "PHOTO",
            preserveOriginal: true,
          });
          const fileId = upload?.fileId || upload?.file?.id || upload?.file?.fileId || null;
          if (fileId) uploadedFileIds.push(String(fileId));
        }
        if (!uploadedFileIds.length) throw new Error("No file id returned from upload");

        setIdentitySubmissionStep("Submitting identity verification...");
        const verification = await submitIdentityVerification(profile.id, documentType, uploadedFileIds);
        setIdentitySubmissionStep("Refreshing profile...");
        setProfile((prev) => ({ ...prev, userIdentityVerification: verification || prev?.userIdentityVerification }));
        await loadProfile();
        if (isOwnProfile) await refreshAuth();
        toast.success("Identity verification submitted");
        return true;
      } catch (error) {
        const status = Number(error?.status || error?.response?.status || error?.response?.data?.code);
        const errorMessage =
          error?.userMessage ||
          error?.apiMessage ||
          error.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          "Failed to submit identity verification";
        if (status === 400 || error?.isBadRequest) {
          Modal.error({
            title: "Invalid document",
            content: errorMessage,
            okText: "OK",
          });
        } else if (status >= 500 || error?.isServerError) {
          Modal.error({
            title: "Upload failed",
            content: "Something went wrong. Please try again.",
            okText: "OK",
          });
        } else {
          Modal.error({
            title: "Upload failed",
            content: errorMessage,
            okText: "OK",
          });
        }
        return false;
      } finally {
        setSubmittingIdentity(false);
        setIdentitySubmissionStep("");
      }
    },
    [isOwnProfile, loadProfile, profile?.id, refreshAuth, submitIdentityVerification, uploadFile],
  );

  const startVerification = useCallback(
    async (channel) => {
      if (channel === "email" && !emailValue) {
        toast.error("Add an email address before verification.");
        return;
      }

      if (channel === "phone" && !phoneValue) {
        toast.error("Add a phone number before verification.");
        return;
      }

      setVerificationLoading((current) => ({ ...current, [channel]: true }));
      try {
        const result = channel === "email"
          ? await startEmailVerification()
          : await startPhoneVerification(phoneValue);
        setVerificationCode("");
        setVerificationModal({
          channel,
          otpToken: result?.otpToken || result?.token || "",
          maskedDestination: result?.maskedDestination || result?.destination || (channel === "email" ? emailValue : phoneValue),
        });
        toast.success(`${channel === "email" ? "Email" : "Phone"} verification code sent.`);
      } catch (error) {
        toast.error(error?.response?.data?.message || error.message || `Failed to start ${channel} verification.`);
      } finally {
        setVerificationLoading((current) => ({ ...current, [channel]: false }));
      }
    },
    [emailValue, phoneValue],
  );

  const submitVerificationCode = useCallback(async () => {
    if (!verificationModal?.otpToken || !verificationCode.trim()) return;

    const channel = verificationModal.channel;
    setVerificationLoading((current) => ({ ...current, submit: true }));
    try {
      if (channel === "email") {
        await verifyEmail(verificationModal.otpToken, verificationCode.trim());
      } else {
        await verifyPhone(verificationModal.otpToken, verificationCode.trim());
      }
      setVerificationModal(null);
      setVerificationCode("");
      await loadProfile();
      if (isOwnProfile) await refreshAuth();
      toast.success(`${channel === "email" ? "Email" : "Phone"} verified successfully.`);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Invalid or expired verification code.");
    } finally {
      setVerificationLoading((current) => ({ ...current, submit: false }));
    }
  }, [isOwnProfile, loadProfile, refreshAuth, verificationCode, verificationModal]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 420 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!profile) {
    return (
      <Card className="custom-shadow" style={{ borderRadius: 16 }}>
        <Typography.Text type="danger">Profile not found.</Typography.Text>
      </Card>
    );
  }

  const currentRole = String(selectedWorkspaceUse || profile.currentAppRole || authUser?.currentAppRole || "REQUESTER").toUpperCase();
  const roleLabel = currentRole === "HELPER" ? "Helper Requirements" : "Requester Requirements";
  const requiredItems = requiredPropertiesForRole(currentRole);
  const missingItems = requiredItems.filter((item) => missingRequiredProperty(profile, item.key));
  const missingKeys = new Set(missingItems.map((item) => item.key));
  const helperStatus = cleanValue(profile.helperProfileStatus || profile.helperProfile?.helperStatus || profile.helperProfile?.status);
  const isHelperApproved = helperStatus === "APPROVED";
  const isHelperPending = helperStatus === "PENDING";
  const identityVerification = profile.userIdentityVerification;

  return (
    <div
      className={`project-page${embedded ? " project-page--embedded" : ""}`}
      style={{ background: embedded ? "transparent" : OLIVEEB.bg, padding: embedded ? 0 : 16 }}
    >
      <div
        style={{
          maxWidth: 1040,
          margin: "0 auto",
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between gap-3 flex-wrap"
          style={{
            borderRadius: 20,
            border: "1px solid #D1E9FF",
            background: "linear-gradient(120deg, #F3FFF8 0%, #F7FFFC 48%, #FFFFFF 100%)",
            padding: "20px 22px",
            marginBottom: 18,
          }}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                width: 58,
                height: 58,
                borderRadius: 16,
                display: "grid",
                placeItems: "center",
                background: currentRole === "HELPER" ? "#EFF8FF" : "#ECFDF3",
                color: currentRole === "HELPER" ? OLIVEEB.blue : OLIVEEB.greenDark,
              }}
            >
              <OlitaskHeaderIcon name={currentRole === "HELPER" ? "construct-outline" : "person-outline"} size={30} />
            </div>
            <div>
              <Tag
                style={{
                  borderRadius: 999,
                  borderColor: "#A6F4C5",
                  background: "#ECFDF3",
                  color: OLIVEEB.greenDark,
                  fontWeight: 900,
                  marginInlineEnd: 0,
                }}
              >
                {embedded ? "STAFF PROFILE" : isOwnProfile ? "MY PROFILE" : "USER PROFILE"}
              </Tag>
              <h1 style={{ margin: "8px 0 2px", color: OLIVEEB.text, fontSize: 30, fontWeight: 900, letterSpacing: 0 }}>
                {getDisplayName(profile)}
              </h1>
              <div style={{ color: OLIVEEB.subtext, fontSize: 15, fontWeight: 700 }}>
                Profile Details
              </div>
            </div>
          </div>
          {!embedded ? (
            <Space wrap>
              {canEnterEditMode ? (
                <Button
                  type={profileEditMode ? "primary" : "default"}
                  aria-label={profileEditMode ? "Done editing profile" : "Edit profile"}
                  icon={profileEditMode ? <SaveOutlined /> : <EditOutlined />}
                  onClick={() => setProfileEditMode((current) => !current)}
                  title={profileEditMode ? "Done" : "Edit"}
                  style={{
                    borderRadius: 12,
                    fontWeight: 850,
                    ...(profileEditMode
                      ? { background: OLIVEEB.green, borderColor: OLIVEEB.green }
                      : { color: OLIVEEB.greenDark, borderColor: "#A6F4C5" }),
                  }}
                >
                  {profileEditMode ? "Done" : "Edit"}
                </Button>
              ) : null}
              <Button
                aria-label="Go back"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                className="olitask-profile-back-button"
                title="Back"
              >
              </Button>
            </Space>
          ) : null}
        </div>

        {!embedded ? (
        <SectionCard title="Required For Your Role" defaultOpen={false}>
          <div
            style={{
              border: "1px solid #EAF0F6",
              borderRadius: 16,
              background: "#FBFDFF",
              padding: 14,
            }}
          >
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div
                className="d-inline-flex align-items-center gap-2"
                style={{
                  borderRadius: 999,
                  border: "1px solid #D1E9FF",
                  background: "#F2F7FF",
                  padding: "8px 12px",
                  color: OLIVEEB.text,
                  fontWeight: 900,
                }}
              >
                <OlitaskHeaderIcon name={currentRole === "HELPER" ? "construct-outline" : "person-outline"} size={15} />
                {roleLabel}
              </div>
              <div
                style={{
                  borderRadius: 999,
                  border: `1px solid ${missingItems.length ? "#F7B27A" : "#ABEFC6"}`,
                  background: missingItems.length ? "#FFF4ED" : "#ECFDF3",
                  color: missingItems.length ? "#B54708" : OLIVEEB.greenDark,
                  padding: "8px 12px",
                  fontWeight: 900,
                  fontSize: 12,
                }}
              >
                {missingItems.length ? `${missingItems.length} Need Update` : "All Required Complete"}
              </div>
            </div>
            <div style={{ marginTop: 10, color: OLIVEEB.subtext, fontSize: 13, fontWeight: 700 }}>
              Fields marked as required below are based on your current role.
            </div>
            <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
              {requiredItems.map((item) => {
                const missing = missingKeys.has(item.key);
                return (
                  <div
                    key={item.key}
                    className="d-flex align-items-center justify-content-between gap-2"
                    style={{
                      borderRadius: 14,
                      border: "1px solid #EEF2F6",
                      background: "#fff",
                      padding: "10px 12px",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2" style={{ color: missing ? "#B42318" : OLIVEEB.text, fontWeight: 850 }}>
                      <OlitaskHeaderIcon name={missing ? "alert-circle-outline" : "checkmark-circle"} size={18} />
                      {item.label}
                    </div>
                    <Tag color={missing ? "red" : "green"} style={{ borderRadius: 999, marginInlineEnd: 0, fontWeight: 900 }}>
                      {missing ? "Needs Update" : "Done"}
                    </Tag>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>
        ) : null}

        {currentRole === "HELPER" ? (
          <div style={{ marginBottom: 18 }}>
            {isHelperApproved ? (
              <>
                <Card
                  style={{
                    borderRadius: 18,
                    border: "1px solid #A6F4C5",
                    background: "linear-gradient(120deg, #F3FFF8, #F7FFFC, #FFFFFF)",
                  }}
                  bodyStyle={{ padding: 16 }}
                >
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <Tag style={{ borderRadius: 999, background: "#ECFDF3", borderColor: "#A6F4C5", color: OLIVEEB.greenDark, fontWeight: 900 }}>
                      <OlitaskHeaderIcon name="sparkles-outline" size={14} /> Approved
                    </Tag>
                    <Tag color="green" style={{ borderRadius: 999, fontWeight: 900, marginInlineEnd: 0 }}>Helper Active</Tag>
                  </div>
                  <h2 style={{ margin: "12px 0 6px", color: OLIVEEB.text, fontWeight: 900, fontSize: 22 }}>
                    You're now an approved Helper
                  </h2>
                  <div style={{ color: OLIVEEB.subtext, fontWeight: 700 }}>You can now accept tasks and start earning.</div>
                </Card>
                <div style={{ marginTop: 10, borderRadius: 14, border: "1px solid #D1FADF", background: "#F3FFF8", padding: 12, color: OLIVEEB.greenDark, fontWeight: 850 }}>
                  <OlitaskHeaderIcon name="checkmark-circle" size={18} /> You now have full access to Helper features.
                </div>
              </>
            ) : isHelperPending ? (
              <Card style={{ borderRadius: 18, border: "1px solid #D1E9FF" }} bodyStyle={{ padding: 16 }}>
                <h2 style={{ margin: 0, color: OLIVEEB.text, fontWeight: 900, fontSize: 22 }}>Your request is being reviewed</h2>
                <div style={{ marginTop: 8, color: OLIVEEB.greenDark, fontWeight: 800 }}>
                  Your Helper signup request was submitted successfully and is currently under review.
                </div>
                <div style={{ marginTop: 12, borderRadius: 14, background: "#EFF8FF", border: "1px solid #D1E9FF", padding: 12, color: OLIVEEB.blue, fontWeight: 800 }}>
                  <OlitaskHeaderIcon name="time-outline" size={18} /> You will be notified once your account is approved.
                </div>
              </Card>
            ) : (
              <Card
                style={{
                  borderRadius: 18,
                  border: "1px solid #D1E9FF",
                  background: "linear-gradient(120deg, #F3FFF8, #F5FAFF, #FFFFFF)",
                }}
                bodyStyle={{ padding: 16 }}
              >
                <Tag style={{ borderRadius: 999, background: "#ECFDF3", borderColor: "#A6F4C5", color: OLIVEEB.greenDark, fontWeight: 900 }}>
                  <OlitaskHeaderIcon name="sparkles-outline" size={14} /> Apply to become a Helper
                </Tag>
                <div style={{ marginTop: 12, color: missingItems.length ? OLIVEEB.subtext : OLIVEEB.greenDark, fontWeight: 800 }}>
                  {missingItems.length
                    ? "Complete the required fields below to submit your helper signup request."
                    : "Everything required is complete. You can submit now."}
                </div>
              </Card>
            )}
          </div>
        ) : null}

        <SectionCard title="Account & Photos" hideTopDivider>
          <div className="d-flex flex-wrap gap-3" style={{ marginBottom: 12 }}>
            <ProfilePhotoTile
              title="Profile Photo"
              preview={profilePhoto}
              timestamp={profile.profilePhotoAt}
              iconName="pencil"
              actionColor="rgba(18,183,106,0.95)"
              onPreview={() => setPhotoPreview({ title: "Profile Photo", src: profilePhoto })}
              onUpload={(file) => savePhoto("profilePhoto", file)}
              uploading={uploading}
              editable={canEditProfile}
            />
            {canViewSensitiveProfileInfo ? (
              <ProfilePhotoTile
                title="Security Photo"
                preview={securityPhoto}
                timestamp={profile.securityPhotoAt}
                iconName="camera"
                actionColor="rgba(47,74,227,0.92)"
                onPreview={() => setPhotoPreview({ title: "Security Photo", src: securityPhoto })}
                onUpload={(file) => savePhoto("securityPhoto", file)}
                uploading={uploading}
                editable={canEditSensitiveProfileInfo}
              />
            ) : null}
          </div>

          <MobileFieldRow label="Username" value={profile.username} editable={canEditProfile} onSave={(value) => saveField("username", value)} loading={savingField.username} />
          <MobileFieldRow
            label="Email"
            value={emailValue}
            required
            missing={missingKeys.has("email")}
            verified={isTruthyFlag(profile.emailVerified || authUser?.emailVerified)}
            editable={canEditProfile}
            onVerify={canEditProfile ? () => startVerification("email") : undefined}
            verifying={verificationLoading.email}
            onSave={(value) => saveField("email", value)}
            loading={savingField.email}
          />
          <PhoneFieldRow
            value={phoneValue}
            verified={isTruthyFlag(profile.phoneVerified || authUser?.phoneVerified)}
            onVerify={canEditProfile ? () => startVerification("phone") : undefined}
            verifying={verificationLoading.phone}
            onSave={(value) => saveField("phone", value)}
            loading={savingField.phone}
            editable={canEditProfile}
          />
          {canViewSensitiveProfileInfo ? (
            <SsnFieldRow value={profile.ssn} onSave={(value) => saveField("ssn", value)} loading={savingField.ssn} editable={canEditSensitiveProfileInfo} />
          ) : null}
        </SectionCard>

        <SectionCard title="Bio Data">
          <MobileFieldRow label="First Name" value={profile.firstName} required missing={missingKeys.has("firstName")} editable={canEditProfile} onSave={(value) => saveField("firstName", value)} loading={savingField.firstName} />
          <MobileFieldRow label="Middle Name" value={profile.middleName} editable={canEditProfile} onSave={(value) => saveField("middleName", value)} loading={savingField.middleName} />
          <MobileFieldRow label="Last Name" value={profile.lastName} required missing={missingKeys.has("lastName")} editable={canEditProfile} onSave={(value) => saveField("lastName", value)} loading={savingField.lastName} />
          {canViewSensitiveProfileInfo ? (
            <DateOfBirthFieldRow value={profile.birthDate} required missing={missingKeys.has("birthDate")} editable={canEditSensitiveProfileInfo} onSave={(value) => saveField("birthDate", value)} loading={savingField.birthDate} />
          ) : null}
          <GenderFieldRow value={profile.gender} otherGender={profile.otherGender} required missing={missingKeys.has("gender")} onSave={saveGender} loading={savingField.gender} editable={canEditProfile} />
          <MobileFieldRow label="Status" value={profile.status} editable={false} />
        </SectionCard>

        {canViewSensitiveProfileInfo ? (
          <SectionCard title="Address">
            <AddressEditor
              profile={profile}
              googleMapsApiKey={googleMapsApiKey}
              saving={savingAddress}
              onSave={saveAddress}
              editable={canEditSensitiveProfileInfo}
            />
          </SectionCard>
        ) : null}

        {canViewSensitiveProfileInfo ? (
        <SectionCard title="Identity Verification">
          <div
            className="d-flex align-items-center gap-2"
            style={{
              borderRadius: 16,
              border: "1px solid #D8E8F8",
              background: "linear-gradient(120deg, #F8FBFF, #F4FFF9)",
              padding: "11px 12px",
              marginTop: 6,
              marginBottom: 12,
              boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                background: "rgba(255,255,255,0.8)",
                border: "1px solid #E4E7EC",
                display: "grid",
                placeItems: "center",
                color: OLIVEEB.blue,
                flex: "0 0 auto",
              }}
            >
              <OlitaskHeaderIcon name="shield-checkmark-outline" size={16} />
            </div>
            <div style={{ color: "#5b0d03", fontWeight: 700, fontSize: 13, lineHeight: "19px", flex: 1 }}>
              Upload a valid ID document so we can verify your identity.
            </div>
          </div>
          <IdentityVerificationEditor
            identityVerification={identityVerification}
            submitting={submittingIdentity}
            submissionStep={identitySubmissionStep}
            onSubmit={submitIdentityDocuments}
            editable={canEditSensitiveProfileInfo}
          />
        </SectionCard>
        ) : null}

        {showSecurityTab && canEditProfile ? (
          <SectionCard title="Account Settings">
            <PasswordCard enabled={isOwnProfile} onSubmit={handlePasswordSubmit} loading={savingPassword} />
          </SectionCard>
        ) : null}
      </div>

      <Modal
        open={Boolean(photoPreview?.src)}
        onCancel={() => setPhotoPreview(null)}
        footer={null}
        centered
        width={720}
        title={<span style={{ color: OLIVEEB.greenDark, fontWeight: 900 }}>{photoPreview?.title || "Photo Preview"}</span>}
      >
        {photoPreview?.src ? (
          <div style={{ display: "grid", placeItems: "center", background: "#0f172a", borderRadius: 14, padding: 12 }}>
            <img
              src={photoPreview.src}
              alt={photoPreview.title || "Photo preview"}
              style={{ maxWidth: "100%", maxHeight: "72vh", objectFit: "contain", borderRadius: 10 }}
            />
          </div>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(verificationModal)}
        onCancel={() => {
          setVerificationModal(null);
          setVerificationCode("");
        }}
        onOk={submitVerificationCode}
        confirmLoading={verificationLoading.submit}
        okText="Verify"
        okButtonProps={{ disabled: !verificationCode.trim() }}
        title={
          <span style={{ color: OLIVEEB.greenDark, fontWeight: 900 }}>
            Verify {verificationModal?.channel === "phone" ? "Phone" : "Email"}
          </span>
        }
        centered
      >
        <div style={{ display: "grid", gap: 12 }}>
          <Typography.Text style={{ color: OLIVEEB.subtext, fontWeight: 700 }}>
            Enter the code sent to {verificationModal?.maskedDestination || "your contact"}.
          </Typography.Text>
          <Input
            value={verificationCode}
            onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 8))}
            onPressEnter={submitVerificationCode}
            placeholder="Verification code"
            inputMode="numeric"
            autoComplete="one-time-code"
          />
        </div>
      </Modal>
    </div>
  );
};

export default UserProfilePage;
